import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ChartHeader from './components/ChartHeader';
import ChartToolbar from './components/ChartToolbar';
import TradingChart from './components/TradingChart';
import MarketDataPanel from './components/MarketDataPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MarketAnalysisHub = () => {
  const navigate = useNavigate();
  const [selectedSymbol, setSelectedSymbol] = useState('EURUSD');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h');
  const [selectedTool, setSelectedTool] = useState('cursor');
  const [selectedChartType, setSelectedChartType] = useState('candlestick');
  const [activeIndicators, setActiveIndicators] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');

  // Initialize component
  useEffect(() => {
    // Set page title
    document.title = 'Market Analysis Hub - Zonex Trader';
    
    // Load saved preferences
    const savedPreferences = localStorage.getItem('market-analysis-preferences');
    if (savedPreferences) {
      const prefs = JSON.parse(savedPreferences);
      setSelectedSymbol(prefs?.symbol || 'EURUSD');
      setSelectedTimeframe(prefs?.timeframe || '1h');
      setSelectedChartType(prefs?.chartType || 'candlestick');
      setActiveIndicators(prefs?.indicators || []);
    }

    // Mock connection status monitoring
    const statusInterval = setInterval(() => {
      const statuses = ['connected', 'connected', 'connected', 'connecting'];
      const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
      setConnectionStatus(randomStatus);
    }, 30000);

    return () => {
      clearInterval(statusInterval);
    };
  }, []);

  // Save preferences when they change
  useEffect(() => {
    const preferences = {
      symbol: selectedSymbol,
      timeframe: selectedTimeframe,
      chartType: selectedChartType,
      indicators: activeIndicators
    };
    localStorage.setItem('market-analysis-preferences', JSON.stringify(preferences));
  }, [selectedSymbol, selectedTimeframe, selectedChartType, activeIndicators]);

  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
  };

  const handleTimeframeChange = (timeframe) => {
    setSelectedTimeframe(timeframe);
  };

  const handleToolSelect = (tool) => {
    setSelectedTool(tool);
  };

  const handleChartTypeChange = (chartType) => {
    setSelectedChartType(chartType);
  };

  const handleIndicatorAdd = (indicator) => {
    if (!activeIndicators?.includes(indicator)) {
      setActiveIndicators(prev => [...prev, indicator]);
    }
  };

  const handleIndicatorRemove = (indicator) => {
    setActiveIndicators(prev => prev?.filter(ind => ind !== indicator));
  };

  const handleTemplateLoad = (template) => {
    console.log('Loading template:', template);
    // In a real app, this would load chart configuration
    setActiveIndicators(['sma', 'rsi']); // Mock template loading
  };

  const handleTemplateSave = () => {
    console.log('Saving current template');
    // In a real app, this would save current chart configuration
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleKeyboardShortcuts = (event) => {
    if (event?.ctrlKey || event?.metaKey) {
      switch (event?.key) {
        case 'f':
          event?.preventDefault();
          toggleFullscreen();
          break;
        case 's':
          event?.preventDefault();
          handleTemplateSave();
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleKeyboardShortcuts);
  }, []);

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex flex-col">
        {/* Fullscreen Header */}
        <div className="bg-card border-b border-border p-2 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="font-mono font-semibold text-lg">{selectedSymbol}</span>
            <span className="text-sm text-muted-foreground">{selectedTimeframe}</span>
            <div className={`flex items-center space-x-1 text-xs ${
              connectionStatus === 'connected' ? 'text-green-600' : 'text-amber-600'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 'bg-amber-500'
              } ${connectionStatus === 'connecting' ? 'animate-pulse' : ''}`}></div>
              <span className="capitalize">{connectionStatus}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleTemplateSave}
              iconName="Save"
              title="Save Template (Ctrl+S)"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              iconName="Minimize2"
              title="Exit Fullscreen (Ctrl+F)"
            />
          </div>
        </div>

        {/* Fullscreen Chart */}
        <div className="flex-1">
          <TradingChart
            symbol={selectedSymbol}
            timeframe={selectedTimeframe}
            chartType={selectedChartType}
            indicators={activeIndicators}
            selectedTool={selectedTool}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />
      {/* Main Content */}
      <main className="pb-16 lg:pb-0">
        <div className="h-screen flex flex-col pt-16">
          {/* Chart Header */}
          <ChartHeader
            selectedSymbol={selectedSymbol}
            onSymbolChange={handleSymbolChange}
            selectedTimeframe={selectedTimeframe}
            onTimeframeChange={handleTimeframeChange}
            onTemplateLoad={handleTemplateLoad}
            onTemplateSave={handleTemplateSave}
          />

          {/* Main Trading Interface */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Toolbar - Desktop Only */}
            <div className="hidden lg:block w-48 xl:w-56">
              <ChartToolbar
                onToolSelect={handleToolSelect}
                selectedTool={selectedTool}
                onIndicatorAdd={handleIndicatorAdd}
                onChartTypeChange={handleChartTypeChange}
                selectedChartType={selectedChartType}
              />
            </div>

            {/* Main Chart Area */}
            <div className="flex-1 flex flex-col lg:flex-row">
              <div className="flex-1">
                <TradingChart
                  symbol={selectedSymbol}
                  timeframe={selectedTimeframe}
                  chartType={selectedChartType}
                  indicators={activeIndicators}
                  selectedTool={selectedTool}
                />
              </div>

              {/* Right Panel - Market Data */}
              <div className="w-full lg:w-80 xl:w-96">
                <MarketDataPanel symbol={selectedSymbol} />
              </div>
            </div>
          </div>

          {/* Mobile Toolbar */}
          <div className="lg:hidden bg-card border-t border-border p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant={selectedTool === 'cursor' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleToolSelect('cursor')}
                  iconName="MousePointer"
                />
                <Button
                  variant={selectedTool === 'line' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleToolSelect('line')}
                  iconName="TrendingUp"
                />
                <Button
                  variant={selectedTool === 'fibonacci' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleToolSelect('fibonacci')}
                  iconName="GitBranch"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleIndicatorAdd('sma')}
                  className="text-xs"
                >
                  SMA
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleIndicatorAdd('rsi')}
                  className="text-xs"
                >
                  RSI
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleFullscreen}
                  iconName="Maximize2"
                />
              </div>
            </div>
          </div>

          {/* Active Indicators Bar */}
          {activeIndicators?.length > 0 && (
            <div className="bg-muted/50 border-t border-border p-2">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Active Indicators:</span>
                <div className="flex flex-wrap gap-1">
                  {activeIndicators?.map((indicator) => (
                    <div
                      key={indicator}
                      className="flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                    >
                      <span className="uppercase font-medium">{indicator}</span>
                      <button
                        onClick={() => handleIndicatorRemove(indicator)}
                        className="hover:bg-primary/20 rounded p-0.5"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Connection Status Bar */}
          <div className="bg-card border-t border-border px-4 py-2">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-4">
                <div className={`flex items-center space-x-1 ${
                  connectionStatus === 'connected' ? 'text-green-600' : 'text-amber-600'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    connectionStatus === 'connected' ? 'bg-green-500' : 'bg-amber-500'
                  } ${connectionStatus === 'connecting' ? 'animate-pulse' : ''}`}></div>
                  <span className="capitalize">{connectionStatus}</span>
                </div>
                <span className="text-muted-foreground">
                  Market: {new Date()?.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false 
                  })}
                </span>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-muted-foreground">
                  Last Update: {new Date()?.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit',
                    hour12: false 
                  })}
                </span>
                <span className="text-muted-foreground">
                  © {new Date()?.getFullYear()} Zonex Trader
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketAnalysisHub;